// Shared source of the dynamic incoterms (price zones) published by XYLO-PRICING.
// Fetched once (singleton) and exposed to every screen that used to hardcode the zone
// list: the client `client_type` selector, the CATALOG price suggestion (clientType→zone),
// and the devis/facture Incoterm dropdown. Falls back to the historical hardcoded values
// when the feed is unreachable, so the UI keeps working offline.
import Vue from 'vue'
import axios from '@/axios.js'

// Historical trade-term incoterms (kept; the published zone labels are merged on top).
const NATIVE_INCOTERMS = [
  { value: 'DAP - Delivered at Place', label: 'DAP - Delivered at Place' },
  { value: 'FCA - Free Carrier', label: 'FCA - Free Carrier' },
  // Enlevement a Teiu, terme par defaut du bois de chauffage vendu en Roumanie
  // (Ferdinand, 2026-10-09) : imprime tel quel, sans lieu separe.
  { value: 'FCA Teiu', label: 'FCA Teiu' },
  { value: 'CPT - Carriage Paid To', label: 'CPT - Carriage Paid To' }
]

// Fallbacks matching the previous hardcoded maps exactly (used if /pricing/zones fails).
const FALLBACK_CLIENT_TYPE_OPTIONS = [
  { value: 1, label: '1. EXW' },
  { value: 2, label: '2. DAP BENELUX' },
  { value: 3, label: '3. DAP FR 0%' },
  { value: 4, label: '4. DAP FR +10%' },
  { value: 5, label: '5. DAP FR +20%' },
  { value: 6, label: '6. DAP FR +15%' }
]
const FALLBACK_CLIENT_TYPE_MAP = {
  1: { zone: 'exw', label: 'EXW' },
  2: { zone: 'benelux', label: 'DAP BENELUX' },
  3: { zone: 'dap_fr0', label: 'DAP FR 0%' },
  4: { zone: 'dap_fr10', label: 'DAP FR +10%' },
  5: { zone: 'dap_fr20', label: 'DAP FR +20%' },
  6: { zone: 'dap_fr15', label: 'DAP FR +15%' }
}

// Reactive singleton shared across all components using the mixin.
const store = Vue.observable({ zones: [], loaded: false })
let inflight = null

function loadZones () {
  if (store.loaded) return Promise.resolve(store.zones)
  if (inflight) return inflight
  inflight = axios.get('/pricing/zones')
    .then(r => { store.zones = (r.data && r.data.zones) || []; store.loaded = true; return store.zones })
    .catch(() => { store.zones = []; store.loaded = true; return [] })
    .then(z => { inflight = null; return z })
  return inflight
}

export default {
  created () { loadZones() },
  computed: {
    pricingZones () { return store.zones },

    // For the client form's client_type <select>.
    clientTypeOptions () {
      const z = store.zones
        .filter(x => x.client_type != null)
        .sort((a, b) => a.client_type - b.client_type)
      if (!z.length) return FALLBACK_CLIENT_TYPE_OPTIONS
      return z.map(x => ({ value: x.client_type, label: `${x.client_type}. ${x.incoterm_label || x.label}` }))
    },

    // For the CATALOG price suggestion: client_type → { zone, label }.
    clientTypeMap () {
      const m = {}
      store.zones.forEach(x => {
        if (x.client_type != null) m[x.client_type] = { zone: x.key, label: x.incoterm_label || x.label }
      })
      return Object.keys(m).length ? m : FALLBACK_CLIENT_TYPE_MAP
    },

    // For the devis/facture Incoterm dropdown: native trade terms + published zone labels.
    incotermOptions () {
      const extra = store.zones
        .filter(x => x.incoterm_label)
        .map(x => ({ value: x.incoterm_label, label: x.incoterm_label }))
      const seen = new Set()
      const out = []
      for (const o of [...NATIVE_INCOTERMS, ...extra]) {
        if (!seen.has(o.value)) { seen.add(o.value); out.push(o) }
      }
      return out
    }
  }
}
